import React, { useEffect, useState, useCallback, useRef } from "react";
import { toast } from "sonner";
import {
  Upload, Download, HardDrive, Files as FilesIcon, Lock, FolderUp,
} from "lucide-react";
import {
  drUploadFile, drStats, drKategorien, drUpdateFile,
} from "../lib/api";
import PageHeader from "../components/layout/PageHeader";
import { useZipJob } from "../components/dataroom/ZipJobModal";
import FolderUploadModal from "../components/dataroom/FolderUploadModal";
import DataroomAccordion from "../components/dataroom/DataroomAccordion";

const fmtSize = (b) => {
  if (b == null) return "—";
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  if (b < 1024 * 1024 * 1024) return `${(b / 1024 / 1024).toFixed(1)} MB`;
  return `${(b / 1024 / 1024 / 1024).toFixed(2)} GB`;
};

export default function Dokumente() {
  const [stats, setStats] = useState(null);
  const [kategorien, setKategorien] = useState([]);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const { openZip, modal: zipJobModal } = useZipJob();
  const [folderUploadOpen, setFolderUploadOpen] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  const loadStats = useCallback(async () => {
    try {
      const [s, kt] = await Promise.all([drStats(), drKategorien()]);
      setStats(s);
      setKategorien(kt.items || []);
    } catch {
      toast.error("Konnte Statistik nicht laden");
    }
  }, []);

  useEffect(() => { loadStats(); }, [loadStats]);

  // Triggert Re-Load von Akkordion + Stats nach Upload/Edit
  const refreshAll = useCallback(() => {
    setRefreshKey((k) => k + 1);
    loadStats();
  }, [loadStats]);

  const used = stats?.storage_used_bytes ?? 0;
  const budget = stats?.storage_budget_bytes ?? (3 * 1024 * 1024 * 1024);
  const pct = Math.min(100, Math.round((used / budget) * 100));
  const pctClass = stats?.storage_warn ? "bg-tot" : pct > 70 ? "bg-reaktiv" : "bg-heiss";
  const internCount = stats?.files_intern_aktiv ?? 0;

  return (
    <div data-testid="dokumente-page" className="pb-container px-0 sm:px-4 lg:px-8 py-4 sm:py-8 space-y-6">
      <PageHeader
        eyebrow="Newton Campus · Master-Pool"
        title="Dokumente"
        subtitle="Alle Projekt-Dateien zentral verwaltet. Investor-Freigaben + Magic-Links erfolgen auf /datenraum."
        testid="dokumente-header"
        actions={
          <>
            <button
              data-testid="dok-folder-upload-btn"
              onClick={() => setFolderUploadOpen(true)}
              className="pb-btn-ghost text-xs uppercase tracking-widish min-h-[40px]"
            >
              <FolderUp size={13} /> Ordnerstruktur
            </button>
            <button
              data-testid="dok-zip-all-btn"
              onClick={() => openZip(
                { scope: "all", include_intern: false },
                "Kompletter Datenraum als ZIP"
              )}
              className="pb-btn-ghost text-xs uppercase tracking-widish min-h-[40px]"
            >
              <Download size={13} /> Alles als ZIP
            </button>
            <button
              data-testid="dok-upload-open-btn"
              onClick={() => setUploadOpen(true)}
              className="pb-btn-primary text-xs uppercase tracking-widish font-semibold min-h-[40px]"
            >
              <Upload size={13} /> Datei hochladen
            </button>
          </>
        }
      />

      {/* Stats-Strip */}
      <section className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="dok-stats">
        <div className="pb-kpi">
          <div className="pb-eyebrow"><HardDrive size={11} className="inline mr-1" /> Speicher</div>
          <div className="text-2xl font-display font-bold text-navy mt-1 tabular-nums">{fmtSize(used)}</div>
          <div className="mt-2 h-1.5 bg-navy/10">
            <div className={`h-full ${pctClass}`} style={{ width: `${pct}%` }} />
          </div>
          <div className="text-[10px] text-navy/55 mt-1 font-sans tabular-nums">{pct}% von {fmtSize(budget)}</div>
        </div>
        <div className="pb-kpi">
          <div className="pb-eyebrow"><FilesIcon size={11} className="inline mr-1" /> Aktive Dateien</div>
          <div className="text-2xl font-display font-bold text-navy mt-1 tabular-nums">{stats?.files_aktiv ?? "—"}</div>
          <div className="text-[10px] text-navy/55 mt-1 font-sans">Master-Pool</div>
        </div>
        <div className="pb-kpi">
          <div className="pb-eyebrow"><Lock size={11} className="inline mr-1" /> Intern</div>
          <div className="text-2xl font-display font-bold text-navy mt-1 tabular-nums">{internCount}</div>
          <div className="text-[10px] text-navy/55 mt-1 font-sans">Nie an Investoren</div>
        </div>
        <div className="pb-kpi">
          <div className="pb-eyebrow">Kategorien</div>
          <div className="text-2xl font-display font-bold text-navy mt-1 tabular-nums">{kategorien.length}</div>
          <div className="text-[10px] text-navy/55 mt-1 font-sans">DD-Struktur 1:1</div>
        </div>
      </section>

      {/* Akkordion-Ansicht (Phase 6.8) — ersetzt die alte flache Tabelle */}
      <DataroomAccordion
        refreshKey={refreshKey}
        onEdit={(f) => setEditing(f)}
        onZip={(slug) => openZip(
          { scope: "kategorie", kategorie: slug, include_intern: false },
          `Kategorie „${slug}" als ZIP`
        )}
      />

      {uploadOpen && (
        <UploadModal
          kategorien={kategorien}
          onClose={() => setUploadOpen(false)}
          onUploaded={() => { setUploadOpen(false); refreshAll(); }}
        />
      )}
      {editing && (
        <EditModal
          file={editing}
          kategorien={kategorien}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); refreshAll(); }}
        />
      )}
      {zipJobModal}
      <FolderUploadModal
        open={folderUploadOpen}
        onClose={() => setFolderUploadOpen(false)}
        onComplete={() => {
          setFolderUploadOpen(false);
          refreshAll();
        }}
      />
    </div>
  );
}

// ----------------------- Upload-Modal -----------------------
function UploadModal({ kategorien, onClose, onUploaded }) {
  const fileRef = useRef(null);
  const [file, setFile] = useState(null);
  const [kategorie, setKategorie] = useState(kategorien[0]?.key || "");
  const [beschreibung, setBeschreibung] = useState("");
  const [version, setVersion] = useState("");
  const [uploading, setUploading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!file) { toast.error("Bitte Datei auswählen"); return; }
    setUploading(true);
    try {
      await drUploadFile(file, { kategorie, beschreibung, version });
      toast.success(`„${file.name}" hochgeladen.`);
      onUploaded?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload fehlgeschlagen");
    } finally {
      setUploading(false);
    }
  };

  return (
    <>
      <div onClick={onClose} className="fixed inset-0 bg-navy/30 z-40" />
      <div
        data-testid="dok-upload-modal"
        className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[min(560px,95vw)] bg-white border border-navy/15 z-50"
      >
        <div className="border-b border-navy/10 px-6 py-4">
          <div className="pb-eyebrow">Master-Pool</div>
          <h3 className="font-display font-semibold text-navy text-xl mt-1 tracking-tight">Datei hochladen</h3>
        </div>
        <form onSubmit={handleSubmit} className="px-6 py-5 space-y-4">
          <div>
            <label className="pb-label">Datei (max. 200 MB)</label>
            <input
              ref={fileRef}
              data-testid="dok-upload-file"
              type="file"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              className="block w-full text-sm font-sans mt-1 file:mr-3 file:py-2 file:px-3 file:border-0 file:bg-navy file:text-white file:text-xs file:uppercase file:tracking-wider hover:file:bg-navy/85 cursor-pointer"
            />
          </div>
          <div>
            <label className="pb-label">Kategorie</label>
            <select
              data-testid="dok-upload-kategorie"
              value={kategorie}
              onChange={(e) => setKategorie(e.target.value)}
              className="pb-input--full"
            >
              {kategorien.map((k) => (
                <option key={k.key} value={k.key}>{k.label}</option>
              ))}
            </select>
            <div className="text-[11px] text-navy/55 mt-1 font-sans">
              {kategorie === "99_Intern"
                ? "Interne Files — niemals an Investoren freigegeben."
                : "Default-Sichtbarkeit kann pro Investor im Datenraum-Tab überschrieben werden."}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="pb-label">Version</label>
              <input
                data-testid="dok-upload-version"
                value={version}
                onChange={(e) => setVersion(e.target.value)}
                placeholder="z.B. v1.2"
                className="pb-input--full"
              />
            </div>
            <div>
              <label className="pb-label">Beschreibung</label>
              <input
                data-testid="dok-upload-beschreibung"
                value={beschreibung}
                onChange={(e) => setBeschreibung(e.target.value)}
                placeholder="Optional"
                className="pb-input--full"
              />
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-3 border-t border-navy/10">
            <button type="button" onClick={onClose} className="pb-btn-ghost">Abbrechen</button>
            <button
              type="submit"
              data-testid="dok-upload-submit"
              disabled={!file || uploading}
              className="pb-btn-primary gap-2"
            >
              <Upload size={13} /> {uploading ? "Lädt…" : "Hochladen"}
            </button>
          </div>
        </form>
      </div>
    </>
  );
}

// ----------------------- Edit-Modal -----------------------
function EditModal({ file, kategorien, onClose, onSaved }) {
  const [kategorie, setKategorie] = useState(file.kategorie || "");
  const [beschreibung, setBeschreibung] = useState(file.beschreibung || "");
  const [version, setVersion] = useState(file.version || "");
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      await drUpdateFile(file.id, { kategorie, beschreibung, version });
      toast.success("Metadaten aktualisiert");
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Speichern fehlgeschlagen");
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <div onClick={onClose} className="fixed inset-0 bg-navy/30 z-40" />
      <div
        data-testid="dok-edit-modal"
        className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[min(520px,95vw)] bg-white border border-navy/15 z-50"
      >
        <div className="border-b border-navy/10 px-6 py-4">
          <div className="pb-eyebrow">Bearbeiten</div>
          <h3 className="font-display font-semibold text-navy text-lg mt-1 tracking-tight break-words">{file.filename_original}</h3>
          {file.original_pfad && (
            <div className="text-[11px] text-navy/55 font-sans mt-1">{file.original_pfad}</div>
          )}
        </div>
        <div className="px-6 py-5 space-y-4">
          <div>
            <label className="pb-label">Kategorie</label>
            <select
              data-testid="dok-edit-kategorie"
              value={kategorie}
              onChange={(e) => setKategorie(e.target.value)}
              className="pb-input--full"
            >
              {kategorien.map((k) => (
                <option key={k.key} value={k.key}>{k.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="pb-label">Beschreibung</label>
            <input
              data-testid="dok-edit-beschreibung"
              value={beschreibung}
              onChange={(e) => setBeschreibung(e.target.value)}
              className="pb-input--full"
            />
          </div>
          <div>
            <label className="pb-label">Version</label>
            <input
              data-testid="dok-edit-version"
              value={version}
              onChange={(e) => setVersion(e.target.value)}
              className="pb-input--full"
            />
          </div>
          {file.is_intern && (
            <div className="border-l-[3px] border-l-tot bg-tot/5 px-4 py-2 text-xs text-navy font-sans flex items-start gap-2">
              <Lock size={12} className="mt-0.5 text-tot" />
              <span>Diese Datei ist als INTERN markiert. Sie wird nie an Investoren freigegeben — auch nicht via Magic-Link.</span>
            </div>
          )}
        </div>
        <div className="px-6 py-4 border-t border-navy/10 flex justify-end gap-2">
          <button onClick={onClose} className="pb-btn-ghost">Abbrechen</button>
          <button
            onClick={handleSave}
            disabled={saving}
            data-testid="dok-edit-save"
            className="pb-btn-primary"
          >
            {saving ? "Speichert…" : "Speichern"}
          </button>
        </div>
      </div>
    </>
  );
}
